// 左右側邊欄：拖邊緣調寬度、也能摺成一條窄條（編輯頁、動畫師工作站、導演歌頁、Cue 庫、分享頁、製播會頁共用）。
// 寬度、摺疊狀態各用自己的 key 記在這台電腦（localStorage，讀寫失敗就用預設）；雙擊邊緣回到預設。
// 摺疊只是 CSS 把裡面藏起來（元件不會卸載），草稿、留言框裡的字都還在。
import { useCallback, useEffect, useRef, useState } from 'react';

/** 摺起來的窄條寬度（px） */
export const PANEL_FOLD_W = 32;

/** 把寬度夾在合理範圍：最小 min；最大 max，也不超過視窗的 35%（左右兩欄都拉到最寬，中間 3D 還有 30% 以上） */
export function clampPanelWidth(w: number, min: number, max: number, viewport: number): number {
  const hi = Math.max(min, Math.min(max, Math.floor(viewport * 0.35)));
  if (!Number.isFinite(w)) return min;
  return Math.round(Math.min(hi, Math.max(min, w)));
}

/** 摺疊狀態另外存的 key（寬度的 key 維持原樣，編輯頁以前存的寬度繼續有效） */
export function foldKey(key: string): string { return `${key}_fold`; }

export function readSavedWidth(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function readSavedFold(key: string): boolean {
  try {
    return window.localStorage.getItem(foldKey(key)) === '1';
  } catch {
    return false;
  }
}

export function saveWidth(key: string, w: number | null) {
  try {
    if (w === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(w));
  } catch { /* 存不了就只在這次有效 */ }
}

export function saveFold(key: string, folded: boolean) {
  try {
    if (folded) window.localStorage.setItem(foldKey(key), '1');
    else window.localStorage.removeItem(foldKey(key));
  } catch { /* 存不了就只在這次有效 */ }
}

/** 這一欄在格線裡該佔的寬度（CSS 變數的值）：摺起來 = 窄條；調過 = 調的寬度；都沒有 = undefined（用 CSS 預設） */
export function panelTrack(width: number | null, collapsed: boolean): string | undefined {
  if (collapsed) return `${PANEL_FOLD_W}px`;
  return width ? `${width}px` : undefined;
}

/** 從把手往上找到放它的格線，量出左邊第一欄 / 右邊最後一欄現在的實際寬度；量不到回 0 */
function measureTrack(from: HTMLElement, side: 'left' | 'right'): number {
  const grid = from.closest('.panel-edge')?.parentElement;
  if (!(grid instanceof HTMLElement)) return 0;
  const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').map(parseFloat).filter(n => Number.isFinite(n));
  if (!cols.length) return 0;
  return side === 'left' ? cols[0] : cols[cols.length - 1];
}

function useMatches(query: string | undefined): boolean {
  const get = () => {
    if (!query) return true;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia(query).matches;
  };
  const [on, setOn] = useState(get);
  useEffect(() => {
    if (!query || typeof window.matchMedia !== 'function') { setOn(true); return; }
    const mq = window.matchMedia(query);
    const fn = () => setOn(mq.matches);
    fn();
    mq.addEventListener?.('change', fn);
    return () => mq.removeEventListener?.('change', fn);
  }, [query]);
  return on;
}

export interface PanelOpts {
  /** 'left'：把手在這一欄的右邊（往右拖變寬）；'right'：把手在左邊（往左拖變寬） */
  side: 'left' | 'right';
  min: number;
  max: number;
  /** 只在這個條件下才有把手和摺疊（手機 / 窄螢幕的抽屜、底部面板維持原樣）；不給 = 一直有 */
  enabledQuery?: string;
}

/**
 * width = null 代表用 CSS 的預設寬度（沒調過）。
 * track：格線這一欄要用的寬度（摺起來 = 窄條）；沒調過、沒摺 = undefined。窄螢幕（active = false）一律 undefined。
 */
export function usePanelWidth(key: string, opts: PanelOpts) {
  const { side, min, max, enabledQuery } = opts;
  const active = useMatches(enabledQuery);
  const [rawWidth, setWidth] = useState<number | null>(() => readSavedWidth(key));
  const [folded, setFolded] = useState<boolean>(() => readSavedFold(key));
  const [vw, setVw] = useState(() => (typeof window === 'undefined' ? 1920 : window.innerWidth));
  const drag = useRef<{ x: number; w: number } | null>(null);

  // 視窗變窄 → 存的寬度自動收回範圍內（不改存的值，視窗變大又回來）
  useEffect(() => {
    const fn = () => setVw(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const width = rawWidth === null ? null : clampPanelWidth(rawWidth, min, max, vw);
  const collapsed = active && folded;

  const setCollapsed = useCallback((v: boolean) => { setFolded(v); saveFold(key, v); }, [key]);
  const toggle = useCallback(() => { setFolded(f => { saveFold(key, !f); return !f; }); }, [key]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const w = measureTrack(e.currentTarget, side) || (width ?? min);
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, w };
  }, [side, width, min]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    setWidth(clampPanelWidth(side === 'left' ? d.w + dx : d.w - dx, min, max, window.innerWidth));
  }, [side, min, max]);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* 已經放開 */ }
    setWidth(w => { saveWidth(key, w); return w; });
  }, [key]);

  const reset = useCallback(() => { setWidth(null); saveWidth(key, null); }, [key]);

  // 鍵盤：把手上按左右鍵微調（每次 16px），Home 回預設
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Home') { e.preventDefault(); reset(); return; }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const cur = measureTrack(e.currentTarget, side) || (width ?? min);
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = clampPanelWidth(cur + (side === 'left' ? dir : -dir) * 16, min, max, window.innerWidth);
    setWidth(next);
    saveWidth(key, next);
  }, [side, width, min, max, key, reset]);

  return {
    width,
    collapsed,
    active,
    side,
    setCollapsed,
    toggle,
    track: active ? panelTrack(width, collapsed) : undefined,
    /** 接在 aside 的 className 後面：摺起來時整欄內容用 CSS 藏起來（不卸載） */
    className: collapsed ? ' is-panel-collapsed' : '',
    handleProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onDoubleClick: reset, onKeyDown },
  };
}

export type PanelState = ReturnType<typeof usePanelWidth>;
